// VerifyEmailModal — the /my-profile email row's "Verify email" flow, send step
// only: POST users/send-email-verification-otp mails a 6-digit code to the
// address the server holds. The field is disabled and shows the SAVED profile
// email, never the in-progress draft — the request carries no body at all, so
// what is displayed is a confirmation of what the server will verify, not an
// input. There is no code-entry screen: the mail carries a /verify-email?code=
// link, and VerifyEmailPage posts the code straight from the URL.

import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Field, Input, Modal } from '@/components/common';
import { useSendEmailVerificationOtpMutation } from '@/api/endpoints/ownProfileApi';
import useOtpCooldown from '@/hooks/useOtpCooldown';
import { emailOtpKey } from '@/redux/otp/otpKeys';

interface VerifyEmailModalProps {
  open: boolean;
  onClose: () => void;
  /** The saved address, passed in by the page. */
  email: string;
}

const VerifyEmailModal = ({ open, onClose, email }: VerifyEmailModalProps) => {
  const { t } = useTranslation();
  const [sendOtp, { isLoading }] = useSendEmailVerificationOtpMutation();

  // A code mailed moments ago still cools this address — reopening the modal
  // shows the countdown instead of allowing another send.
  const cooldown = useOtpCooldown(email ? emailOtpKey(email) : null);

  const send = async () => {
    if (cooldown.active) return;
    try {
      await sendOtp(email).unwrap();
      toast.success(t('profile_code_sent'));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon='mail'
      title={t('profile_verify_email_title')}
      subtitle={t('profile_verify_email_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isLoading}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='send' onClick={send} loading={isLoading} disabled={cooldown.active}>
            {cooldown.active
              ? t('profile_send_code_in', { time: cooldown.label })
              : t('profile_send_code')}
          </Button>
        </>
      }
    >
      <Field label={t('profile_email_address')} htmlFor='profile-verify-email'>
        <Input id='profile-verify-email' type='email' value={email} dir='ltr' disabled readOnly />
      </Field>
    </Modal>
  );
};

export default VerifyEmailModal;
